import {
  BodyText,
  Field,
  FieldError,
  FieldLabel,
  type ItemOptionProps,
  RadioGroup,
  Search,
  Textarea,
} from "@/components/ui";

import { SubmitCancelButtons } from "@/features/Patient/MedicalRecords/components/SubmitCancelButtons";
import { AllergyIntolerance } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "@tanstack/react-router";
import { BadgeMinus, SearchIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useDebounce } from "react-use";
import type { z } from "zod";
import {
  useCommonAllergies,
  useCreateAllergy,
  useSearchAllergies,
} from "../../network/useAllergies";
import { createAllergyFormSchema } from "../../schemas/schemas";
import type { AllergyIntoleranceTypeValue } from "../../types/allergies";
import { ReactionRow } from "./ReactionRow";

interface SearchResultMetadata {
  system?: string;
}

interface AddAllergyFormProps {
  closeForm: () => void;
}

export const AddAllergyForm = ({ closeForm }: AddAllergyFormProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const ref = useRef<HTMLDivElement>(null);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  useDebounce(
    () => {
      setDebouncedSearchTerm(searchTerm);
    },
    200,
    [searchTerm],
  );
  const { isLoading: isSearchLoading, data: allergySearchResults } =
    useSearchAllergies(debouncedSearchTerm);

  const { data: commonAllergies } = useCommonAllergies();

  const createSearchOptions = (
    allergies: typeof allergySearchResults,
  ): ItemOptionProps[] =>
    allergies?.map((allergy) => ({
      label: allergy.allergyName,
      value: allergy.allergyCode,
      metadata: {
        system: allergy.allergyCodeSystem,
      } as SearchResultMetadata,
    })) || [];

  const searchResults = createSearchOptions(allergySearchResults);
  const commonAllergyOptions = createSearchOptions(commonAllergies);

  const displayOptions = debouncedSearchTerm
    ? searchResults
    : commonAllergyOptions;

  const form = useForm<z.infer<typeof createAllergyFormSchema>>({
    resolver: zodResolver(createAllergyFormSchema),
    defaultValues: {
      allergyType: AllergyIntolerance.AllergyIntoleranceType.ALLERGY,
      reactions: [{ id: undefined, severity: "", description: "" }],
    },
  });

  const { fields: reactionFields } = useFieldArray({
    control: form.control,
    name: "reactions",
  });

  const handleItemSelected = (item?: ItemOptionProps) => {
    form.setValue("allergyCode", item?.value?.toString() || "", {
      shouldValidate: true,
    });
    form.setValue("allergyName", item?.label || "");
    form.setValue(
      "allergyCodeSystem",
      (item?.metadata as SearchResultMetadata)?.system || "",
    );
  };

  const handleSubmit = (values: z.infer<typeof createAllergyFormSchema>) => {
    const filteredReactions = values.reactions
      ?.filter((r) => r.severity || r.description)
      .map((r) => ({
        ...r,
        severity: r.severity || undefined,
      }));

    createAllergy({
      allergyCode: values.allergyCode,
      allergyCodeSystem: values.allergyCodeSystem,
      allergyName: values.allergyName,
      allergyType: values.allergyType,
      note: values.notes,
      reactions: filteredReactions?.length ? filteredReactions : undefined,
    });
  };

  const { isPending: isSavingCreation, mutate: createAllergy } =
    useCreateAllergy({ patientId, onSuccess: closeForm });

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, []);

  return (
    <>
      <div ref={ref} />
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex flex-col gap-2 border border-primary p-3 rounded-md bg-error">
          <BodyText size="sm" bold>
            New Allergy
          </BodyText>
          <Controller
            control={form.control}
            name="allergyType"
            render={({ field, fieldState }) => (
              <Field className="w-full flex flex-1 flex-col">
                <FieldLabel htmlFor="allergyType">Allergy Type*</FieldLabel>
                <RadioGroup
                  value={field.value}
                  onValueChange={(e) => {
                    form.setValue(
                      "allergyType",
                      e as AllergyIntoleranceTypeValue,
                    );
                  }}
                  options={[
                    {
                      value: AllergyIntolerance.AllergyIntoleranceType.ALLERGY,
                      label: "Allergy",
                    },
                    {
                      value:
                        AllergyIntolerance.AllergyIntoleranceType.INTOLERANCE,
                      label: "Intolerance",
                    },
                  ]}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <Controller
            control={form.control}
            name="allergyCode"
            render={({ fieldState }) => (
              <Field className="w-full flex flex-1 flex-col">
                <FieldLabel htmlFor="allergyCode">Allergy*</FieldLabel>
                <Search
                  autoFocusInput
                  autoFocusInputDelay={100}
                  input={searchTerm}
                  leftIcon={SearchIcon}
                  isLoading={isSearchLoading && !!debouncedSearchTerm}
                  showLoadingSpinner={true}
                  options={displayOptions}
                  openOnFocus
                  openWithoutInput
                  onInputChange={(searchString) => {
                    setSearchTerm(searchString);
                    handleItemSelected(undefined);
                  }}
                  onItemSelected={(item) => {
                    handleItemSelected(item);
                  }}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <div className="flex flex-col gap-2">
            {reactionFields.map((field, index) => (
              <Controller
                key={field.id}
                control={form.control}
                name={`reactions.${index}`}
                render={({ field: reactionField }) => (
                  <ReactionRow
                    index={index}
                    severity={reactionField.value?.severity}
                    description={reactionField.value?.description}
                    onSeverityChange={(val) =>
                      reactionField.onChange({
                        ...reactionField.value,
                        severity: val,
                      })
                    }
                    onDescriptionChange={(val) =>
                      reactionField.onChange({
                        ...reactionField.value,
                        description: val,
                      })
                    }
                    onRemove={() =>
                      reactionField.onChange({
                        id: undefined,
                        severity: "",
                        description: "",
                      })
                    }
                    actionIcon={BadgeMinus}
                    actionAriaLabel="Clear reaction"
                    showAction={
                      !!reactionField.value?.severity ||
                      !!reactionField.value?.description
                    }
                  />
                )}
              />
            ))}
          </div>
          <Controller
            control={form.control}
            name="notes"
            render={({ field, fieldState }) => (
              <Field className="w-full flex flex-1 flex-col">
                <FieldLabel htmlFor="notes">Notes</FieldLabel>
                <Textarea {...field} id="notes" placeholder="Enter notes" />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <SubmitCancelButtons
            isSaving={isSavingCreation}
            onCancel={closeForm}
          />
        </div>
      </form>
    </>
  );
};
